// ===================================================================
// KAIXO - PlansModal — Pantalla de paquetes/planes
// ===================================================================
// En modo BETA: el botón dice "Activar (beta)" y al pulsarlo se activa
// directamente sin pasar por pago.
// En modo producción: el botón dice "Pagar $X" y redirige a Stripe.
// ===================================================================

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import { Icons } from '../Icons/Icons';
import usePlan from '../../hooks/usePlan';
import useModalClose from '../../hooks/useModalClose';
import friendlyError from '../../utils/friendlyError';
import './PlansModal.css';

// Diccionario i18n local — 7 idiomas con más volumen + fallback a inglés.
const I18N = {
    español: {
        title: 'Elige tu plan',
        beta: 'Modo beta — activación inmediata sin pago. Prueba todas las funciones.',
        freeLeft: (n) => `Quedan ${n.toLocaleString()} plazas FREE disponibles`,
        freeOut: 'Plazas FREE agotadas — elige un plan de pago para entrar',
        close: 'Cerrar',
        loading: 'Cargando planes...',
        loadFail: 'No se pudieron cargar los planes',
        activating: 'Activando...',
        activeNow: 'Plan activo',
        activateFree: 'Activar gratis',
        activateBeta: 'Activar (beta)',
        pay: 'Pagar',
        recommended: 'Recomendado',
        currentPlan: 'Tu plan actual',
        free: 'Gratis',
        perMonth: '/mes',
        footer: 'Cualquier cambio se aplica al instante si es upgrade (con prorrateo automático), o al vencimiento si es downgrade.',
        cancelSub: 'Cancelar auto-renovación',
        cancelling: 'Cancelando…',
        cancelHint: 'Mantendrás el plan hasta su vencimiento; después pasarás a FREE.',
        cancelConfirm: '¿Cancelar la auto-renovación? Mantendrás el plan hasta su fecha de vencimiento; después pasarás automáticamente a FREE.',
        cancelOk: 'Auto-renovación cancelada. Tu plan sigue activo hasta el vencimiento.',
        cancelFail: 'No se pudo cancelar — reintenta en un momento',
        activateFail: 'No se pudo activar el plan',
        activatedMsg: (id) => `¡Plan ${id} activado!`,
        upgradedMsg: (id, c) => `¡Plan actualizado a ${id}! Crédito aplicado: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `Bajada programada a ${id} al vencer tu plan actual.`,
        activatingShort: (id) => `⏱ Activando ${id}...`
    },
    inglés: {
        title: 'Choose your plan',
        beta: 'Beta mode — instant activation, no payment. Try all features.',
        freeLeft: (n) => `${n.toLocaleString()} FREE seats left`,
        freeOut: 'FREE seats sold out — pick a paid plan to enter',
        close: 'Close',
        loading: 'Loading plans...',
        loadFail: 'Could not load plans',
        activating: 'Activating...',
        activeNow: 'Active plan',
        activateFree: 'Activate free',
        activateBeta: 'Activate (beta)',
        pay: 'Pay',
        recommended: 'Recommended',
        currentPlan: 'Your current plan',
        free: 'Free',
        perMonth: '/month',
        footer: 'Any change applies instantly if upgrade (with automatic proration), or at the end of the period if downgrade.',
        cancelSub: 'Cancel auto-renewal',
        cancelling: 'Cancelling…',
        cancelHint: 'You keep the plan until expiration; then you go back to FREE.',
        cancelConfirm: 'Cancel auto-renewal? You keep the plan until its end date, then you switch to FREE automatically.',
        cancelOk: 'Auto-renewal cancelled. Your plan stays active until expiration.',
        cancelFail: 'Could not cancel — try again in a moment',
        activateFail: 'Could not activate plan',
        activatedMsg: (id) => `Plan ${id} activated!`,
        upgradedMsg: (id, c) => `Upgraded to ${id}! Credit applied: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `Downgrade to ${id} scheduled at the end of your current plan.`,
        activatingShort: (id) => `⏱ Activating ${id}...`
    },
    francés: {
        title: 'Choisis ton plan',
        beta: 'Mode bêta — activation immédiate sans paiement. Teste tout.',
        freeLeft: (n) => `${n.toLocaleString()} places FREE disponibles`,
        freeOut: 'Plus de places FREE — choisis un plan payant',
        close: 'Fermer',
        loading: 'Chargement des plans...',
        loadFail: 'Impossible de charger les plans',
        activating: 'Activation...',
        activeNow: 'Plan actif',
        activateFree: 'Activer gratuit',
        activateBeta: 'Activer (bêta)',
        pay: 'Payer',
        recommended: 'Recommandé',
        currentPlan: 'Ton plan actuel',
        free: 'Gratuit',
        perMonth: '/mois',
        footer: 'Tout changement s\'applique immédiatement en cas d\'upgrade (avec prorata) ou à l\'échéance en cas de downgrade.',
        cancelSub: 'Annuler le renouvellement',
        cancelling: 'Annulation…',
        cancelHint: 'Tu gardes le plan jusqu\'à l\'expiration ; ensuite tu repasses à FREE.',
        cancelConfirm: 'Annuler le renouvellement automatique ? Tu gardes le plan jusqu\'à sa date de fin, puis FREE.',
        cancelOk: 'Renouvellement annulé. Ton plan reste actif jusqu\'à l\'expiration.',
        cancelFail: 'Impossible d\'annuler — réessaie dans un instant',
        activateFail: 'Impossible d\'activer le plan',
        activatedMsg: (id) => `Plan ${id} activé !`,
        upgradedMsg: (id, c) => `Upgradé à ${id} ! Crédit appliqué : ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `Downgrade à ${id} programmé à la fin du plan actuel.`,
        activatingShort: (id) => `⏱ Activation de ${id}...`
    },
    alemán: {
        title: 'Wähle deinen Tarif',
        beta: 'Beta-Modus — sofortige Aktivierung, keine Zahlung. Teste alles.',
        freeLeft: (n) => `${n.toLocaleString()} FREE-Plätze übrig`,
        freeOut: 'FREE-Plätze ausverkauft — wähle einen Bezahltarif',
        close: 'Schließen',
        loading: 'Tarife werden geladen...',
        loadFail: 'Tarife konnten nicht geladen werden',
        activating: 'Aktivierung...',
        activeNow: 'Aktiver Tarif',
        activateFree: 'Kostenlos aktivieren',
        activateBeta: 'Aktivieren (Beta)',
        pay: 'Zahlen',
        recommended: 'Empfohlen',
        currentPlan: 'Dein aktueller Tarif',
        free: 'Kostenlos',
        perMonth: '/Monat',
        footer: 'Jede Änderung gilt sofort bei Upgrade (mit Anpassung) oder erst am Ende des Zeitraums bei Downgrade.',
        cancelSub: 'Auto-Verlängerung kündigen',
        cancelling: 'Kündigung…',
        cancelHint: 'Du behältst den Tarif bis zum Ablauf; danach FREE.',
        cancelConfirm: 'Auto-Verlängerung kündigen? Du behältst den Tarif bis zum Ende, dann FREE.',
        cancelOk: 'Auto-Verlängerung gekündigt. Tarif bleibt bis Ablauf aktiv.',
        cancelFail: 'Konnte nicht kündigen — versuche es gleich erneut',
        activateFail: 'Tarif konnte nicht aktiviert werden',
        activatedMsg: (id) => `Tarif ${id} aktiviert!`,
        upgradedMsg: (id, c) => `Auf ${id} upgegradet! Gutschrift: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `Downgrade auf ${id} am Ende des aktuellen Tarifs geplant.`,
        activatingShort: (id) => `⏱ Aktiviere ${id}...`
    },
    portugués: {
        title: 'Escolhe o teu plano',
        beta: 'Modo beta — ativação imediata sem pagamento. Testa tudo.',
        freeLeft: (n) => `Restam ${n.toLocaleString()} vagas FREE`,
        freeOut: 'Vagas FREE esgotadas — escolhe um plano pago',
        close: 'Fechar',
        loading: 'A carregar planos...',
        loadFail: 'Não foi possível carregar os planos',
        activating: 'Ativando...',
        activeNow: 'Plano ativo',
        activateFree: 'Ativar grátis',
        activateBeta: 'Ativar (beta)',
        pay: 'Pagar',
        recommended: 'Recomendado',
        currentPlan: 'O teu plano atual',
        free: 'Grátis',
        perMonth: '/mês',
        footer: 'Qualquer alteração aplica-se de imediato em upgrade (com proporcional) ou no fim do período em downgrade.',
        cancelSub: 'Cancelar renovação automática',
        cancelling: 'Cancelando…',
        cancelHint: 'Manténs o plano até expirar; depois passas a FREE.',
        cancelConfirm: 'Cancelar a renovação automática? Manténs o plano até ao fim, depois FREE.',
        cancelOk: 'Renovação cancelada. Plano continua ativo até expirar.',
        cancelFail: 'Não foi possível cancelar — tenta de novo',
        activateFail: 'Não foi possível ativar o plano',
        activatedMsg: (id) => `Plano ${id} ativado!`,
        upgradedMsg: (id, c) => `Atualizado para ${id}! Crédito: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `Mudança para ${id} agendada para o fim do plano atual.`,
        activatingShort: (id) => `⏱ A ativar ${id}...`
    },
    italiano: {
        title: 'Scegli il tuo piano',
        beta: 'Modalità beta — attivazione immediata senza pagamento. Prova tutto.',
        freeLeft: (n) => `${n.toLocaleString()} posti FREE rimasti`,
        freeOut: 'Posti FREE esauriti — scegli un piano a pagamento',
        close: 'Chiudi',
        loading: 'Caricamento piani...',
        loadFail: 'Impossibile caricare i piani',
        activating: 'Attivazione...',
        activeNow: 'Piano attivo',
        activateFree: 'Attiva gratis',
        activateBeta: 'Attiva (beta)',
        pay: 'Paga',
        recommended: 'Consigliato',
        currentPlan: 'Il tuo piano attuale',
        free: 'Gratis',
        perMonth: '/mese',
        footer: 'Le modifiche si applicano subito in upgrade (con conguaglio) o a fine periodo in downgrade.',
        cancelSub: 'Annulla rinnovo automatico',
        cancelling: 'Annullamento…',
        cancelHint: 'Mantieni il piano fino alla scadenza; dopo passi a FREE.',
        cancelConfirm: 'Annullare il rinnovo automatico? Mantieni il piano fino alla scadenza, poi FREE.',
        cancelOk: 'Rinnovo annullato. Il piano resta attivo fino alla scadenza.',
        cancelFail: 'Impossibile annullare — riprova fra poco',
        activateFail: 'Impossibile attivare il piano',
        activatedMsg: (id) => `Piano ${id} attivato!`,
        upgradedMsg: (id, c) => `Aggiornato a ${id}! Credito: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `Passaggio a ${id} programmato alla fine del piano attuale.`,
        activatingShort: (id) => `⏱ Attivazione ${id}...`
    },
    euskera: {
        title: 'Aukeratu zure plana',
        beta: 'Beta modua — berehalako aktibazioa, ordainketarik gabe. Probatu dena.',
        freeLeft: (n) => `${n.toLocaleString()} FREE leku geratzen dira`,
        freeOut: 'FREE lekuak agortuta — aukeratu ordaindutako plana',
        close: 'Itxi',
        loading: 'Planak kargatzen...',
        loadFail: 'Ezin izan dira planak kargatu',
        activating: 'Aktibatzen...',
        activeNow: 'Plan aktiboa',
        activateFree: 'Doan aktibatu',
        activateBeta: 'Aktibatu (beta)',
        pay: 'Ordaindu',
        recommended: 'Gomendatua',
        currentPlan: 'Zure egungo plana',
        free: 'Doan',
        perMonth: '/hil',
        footer: 'Aldaketak berehala aplikatzen dira upgradean (proporzionalki), edo aldiaren amaieran downgradean.',
        cancelSub: 'Berriztatze automatikoa bertan behera utzi',
        cancelling: 'Bertan behera uzten…',
        cancelHint: 'Plana iraungitu arte mantenduko duzu; ondoren FREE.',
        cancelConfirm: 'Berriztatze automatikoa bertan behera utzi? Plana amaitu arte gordeko duzu, gero FREE.',
        cancelOk: 'Berriztatzea bertan behera utzi da. Plana iraungi arte aktibo geratzen da.',
        cancelFail: 'Ezin izan da bertan behera utzi — saiatu berriro',
        activateFail: 'Ezin izan da plana aktibatu',
        activatedMsg: (id) => `${id} plana aktibatuta!`,
        upgradedMsg: (id, c) => `${id}-ra eguneratuta! Kreditua: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `${id}-ra jaistea programatuta egungo planaren amaieran.`,
        activatingShort: (id) => `⏱ ${id} aktibatzen...`
    },
    chino: {
        title: '选择您的套餐',
        beta: '测试模式 — 立即激活，无需付款。试用所有功能。',
        freeLeft: (n) => `剩余 ${n.toLocaleString()} 个免费名额`,
        freeOut: '免费名额已售罄 — 请选择付费套餐',
        close: '关闭',
        loading: '加载套餐中...',
        loadFail: '无法加载套餐',
        activating: '激活中...',
        activeNow: '当前套餐',
        activateFree: '免费激活',
        activateBeta: '激活（测试版）',
        pay: '支付',
        recommended: '推荐',
        currentPlan: '您的当前套餐',
        free: '免费',
        perMonth: '/月',
        footer: '升级立即生效（自动按比例计算），降级在当前周期结束时生效。',
        cancelSub: '取消自动续订',
        cancelling: '取消中…',
        cancelHint: '套餐保留至到期，之后转为免费套餐。',
        cancelConfirm: '取消自动续订？您将保留当前套餐至到期日，然后自动转为免费套餐。',
        cancelOk: '自动续订已取消。套餐保持有效直到到期。',
        cancelFail: '无法取消 — 请稍后重试',
        activateFail: '无法激活套餐',
        activatedMsg: (id) => `套餐 ${id} 已激活！`,
        upgradedMsg: (id, c) => `已升级到 ${id}！抵扣金额：${c.toFixed(2)}€。`,
        downgradeMsg: (id) => `降级到 ${id} 已安排在当前套餐结束时生效。`,
        activatingShort: (id) => `⏱ 正在激活 ${id}...`
    },
    japonés: {
        title: 'プランを選択',
        beta: 'ベータモード — 即時有効化、支払い不要。すべての機能を試せます。',
        freeLeft: (n) => `残り ${n.toLocaleString()} 件の無料枠`,
        freeOut: '無料枠は売り切れ — 有料プランをお選びください',
        close: '閉じる',
        loading: 'プランを読み込み中...',
        loadFail: 'プランを読み込めませんでした',
        activating: '有効化中...',
        activeNow: '有効なプラン',
        activateFree: '無料で有効化',
        activateBeta: '有効化（ベータ）',
        pay: '支払う',
        recommended: 'おすすめ',
        currentPlan: '現在のプラン',
        free: '無料',
        perMonth: '/月',
        footer: 'アップグレードは即時適用（自動日割り）、ダウングレードは期間終了時に適用されます。',
        cancelSub: '自動更新をキャンセル',
        cancelling: 'キャンセル中…',
        cancelHint: 'プランは有効期限まで利用可能、その後は無料プランに戻ります。',
        cancelConfirm: '自動更新をキャンセルしますか？プランは終了日まで有効、その後無料プランに切り替わります。',
        cancelOk: '自動更新がキャンセルされました。プランは有効期限まで有効です。',
        cancelFail: 'キャンセルできませんでした — しばらくしてから再試行してください',
        activateFail: 'プランを有効化できませんでした',
        activatedMsg: (id) => `${id} プランが有効化されました！`,
        upgradedMsg: (id, c) => `${id} にアップグレードしました！クレジット適用：${c.toFixed(2)}€。`,
        downgradeMsg: (id) => `現在のプラン終了時に ${id} へのダウングレードが予定されています。`,
        activatingShort: (id) => `⏱ ${id} を有効化中...`
    },
    árabe: {
        title: 'اختر خطتك',
        beta: 'وضع تجريبي — تفعيل فوري بدون دفع. جرب كل الميزات.',
        freeLeft: (n) => `${n.toLocaleString()} مقعد مجاني متبقٍ`,
        freeOut: 'انتهت المقاعد المجانية — اختر خطة مدفوعة',
        close: 'إغلاق',
        loading: 'جاري تحميل الخطط...',
        loadFail: 'تعذر تحميل الخطط',
        activating: 'جاري التفعيل...',
        activeNow: 'الخطة النشطة',
        activateFree: 'تفعيل مجاني',
        activateBeta: 'تفعيل (تجريبي)',
        pay: 'دفع',
        recommended: 'موصى به',
        currentPlan: 'خطتك الحالية',
        free: 'مجاني',
        perMonth: '/شهر',
        footer: 'يُطبق أي تغيير فوراً عند الترقية (مع التناسب التلقائي)، أو في نهاية الفترة عند التخفيض.',
        cancelSub: 'إلغاء التجديد التلقائي',
        cancelling: 'جاري الإلغاء…',
        cancelHint: 'تحتفظ بالخطة حتى انتهائها؛ ثم تنتقل إلى المجاني.',
        cancelConfirm: 'إلغاء التجديد التلقائي؟ ستحتفظ بالخطة حتى تاريخ انتهائها، ثم تنتقل إلى المجاني تلقائياً.',
        cancelOk: 'تم إلغاء التجديد التلقائي. خطتك تبقى نشطة حتى انتهائها.',
        cancelFail: 'تعذر الإلغاء — حاول مرة أخرى بعد لحظات',
        activateFail: 'تعذر تفعيل الخطة',
        activatedMsg: (id) => `تم تفعيل خطة ${id}!`,
        upgradedMsg: (id, c) => `تمت الترقية إلى ${id}! الرصيد المطبق: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `تم جدولة التخفيض إلى ${id} في نهاية خطتك الحالية.`,
        activatingShort: (id) => `⏱ جاري تفعيل ${id}...`
    },
    hindi: {
        title: 'अपना प्लान चुनें',
        beta: 'बीटा मोड — तत्काल सक्रियण, कोई भुगतान नहीं। सभी फीचर्स आज़माएं।',
        freeLeft: (n) => `${n.toLocaleString()} फ्री सीटें बची हैं`,
        freeOut: 'फ्री सीटें खत्म — कोई पेड प्लान चुनें',
        close: 'बंद करें',
        loading: 'प्लान लोड हो रहे हैं...',
        loadFail: 'प्लान लोड नहीं हो सके',
        activating: 'सक्रिय हो रहा है...',
        activeNow: 'सक्रिय प्लान',
        activateFree: 'मुफ्त सक्रिय करें',
        activateBeta: 'सक्रिय करें (बीटा)',
        pay: 'भुगतान करें',
        recommended: 'अनुशंसित',
        currentPlan: 'आपका वर्तमान प्लान',
        free: 'निःशुल्क',
        perMonth: '/माह',
        footer: 'अपग्रेड तुरंत लागू होता है (स्वतः समानुपातिक), डाउनग्रेड वर्तमान अवधि के अंत में।',
        cancelSub: 'ऑटो-नवीकरण रद्द करें',
        cancelling: 'रद्द हो रहा है…',
        cancelHint: 'समाप्ति तक प्लान रहेगा; उसके बाद FREE।',
        cancelConfirm: 'ऑटो-नवीकरण रद्द करें? समाप्ति तिथि तक प्लान रहेगा, फिर अपने आप FREE हो जाएगा।',
        cancelOk: 'ऑटो-नवीकरण रद्द। प्लान समाप्ति तक सक्रिय रहेगा।',
        cancelFail: 'रद्द नहीं हो सका — कृपया कुछ देर में पुनः प्रयास करें',
        activateFail: 'प्लान सक्रिय नहीं हो सका',
        activatedMsg: (id) => `${id} प्लान सक्रिय हुआ!`,
        upgradedMsg: (id, c) => `${id} में अपग्रेड हुआ! क्रेडिट लागू: ${c.toFixed(2)}€।`,
        downgradeMsg: (id) => `${id} में डाउनग्रेड वर्तमान प्लान के अंत में होगा।`,
        activatingShort: (id) => `⏱ ${id} सक्रिय हो रहा है...`
    },
    ruso: {
        title: 'Выберите тариф',
        beta: 'Бета-режим — мгновенная активация, без оплаты. Попробуй всё.',
        freeLeft: (n) => `Осталось ${n.toLocaleString()} мест FREE`,
        freeOut: 'Места FREE закончились — выбери платный тариф',
        close: 'Закрыть',
        loading: 'Загрузка тарифов...',
        loadFail: 'Не удалось загрузить тарифы',
        activating: 'Активация...',
        activeNow: 'Активный тариф',
        activateFree: 'Активировать бесплатно',
        activateBeta: 'Активировать (бета)',
        pay: 'Оплатить',
        recommended: 'Рекомендуется',
        currentPlan: 'Твой текущий тариф',
        free: 'Бесплатный',
        perMonth: '/мес',
        footer: 'Изменения применяются сразу при апгрейде (с авто-пропорцией) или в конце периода при даунгрейде.',
        cancelSub: 'Отменить авто-продление',
        cancelling: 'Отмена…',
        cancelHint: 'Тариф сохраняется до окончания; затем переход на FREE.',
        cancelConfirm: 'Отменить авто-продление? Тариф сохранится до даты окончания, затем автоматически перейдёшь на FREE.',
        cancelOk: 'Авто-продление отменено. Тариф остаётся активным до окончания.',
        cancelFail: 'Не удалось отменить — попробуй ещё раз через момент',
        activateFail: 'Не удалось активировать тариф',
        activatedMsg: (id) => `Тариф ${id} активирован!`,
        upgradedMsg: (id, c) => `Обновлено до ${id}! Применён кредит: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `Понижение до ${id} запланировано на конец текущего тарифа.`,
        activatingShort: (id) => `⏱ Активация ${id}...`
    },
    coreano: {
        title: '요금제 선택',
        beta: '베타 모드 — 즉시 활성화, 결제 불필요. 모든 기능을 시험해보세요.',
        freeLeft: (n) => `남은 FREE 좌석 ${n.toLocaleString()}개`,
        freeOut: 'FREE 좌석 매진 — 유료 요금제를 선택하세요',
        close: '닫기',
        loading: '요금제 로딩 중...',
        loadFail: '요금제를 불러올 수 없습니다',
        activating: '활성화 중...',
        activeNow: '활성 요금제',
        activateFree: '무료 활성화',
        activateBeta: '활성화 (베타)',
        pay: '결제',
        recommended: '추천',
        currentPlan: '현재 요금제',
        free: '무료',
        perMonth: '/월',
        footer: '업그레이드는 즉시 적용 (자동 일할 계산), 다운그레이드는 기간 종료 시 적용됩니다.',
        cancelSub: '자동 갱신 취소',
        cancelling: '취소 중…',
        cancelHint: '만료까지 요금제가 유지되며, 그 후 FREE로 전환됩니다.',
        cancelConfirm: '자동 갱신을 취소하시겠습니까? 만료일까지 요금제가 유지되며, 그 후 자동으로 FREE로 전환됩니다.',
        cancelOk: '자동 갱신이 취소되었습니다. 만료까지 요금제가 활성 상태로 유지됩니다.',
        cancelFail: '취소할 수 없습니다 — 잠시 후 다시 시도하세요',
        activateFail: '요금제를 활성화할 수 없습니다',
        activatedMsg: (id) => `${id} 요금제가 활성화되었습니다!`,
        upgradedMsg: (id, c) => `${id}로 업그레이드되었습니다! 크레딧 적용: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `${id}로 다운그레이드가 현재 요금제 종료 시 예정되어 있습니다.`,
        activatingShort: (id) => `⏱ ${id} 활성화 중...`
    },
    turco: {
        title: 'Planını seç',
        beta: 'Beta modu — anlık aktivasyon, ödeme yok. Her şeyi dene.',
        freeLeft: (n) => `${n.toLocaleString()} FREE yer kaldı`,
        freeOut: 'FREE yerler tükendi — ücretli bir plan seç',
        close: 'Kapat',
        loading: 'Planlar yükleniyor...',
        loadFail: 'Planlar yüklenemedi',
        activating: 'Aktivasyon...',
        activeNow: 'Aktif plan',
        activateFree: 'Ücretsiz aktive et',
        activateBeta: 'Aktive et (beta)',
        pay: 'Öde',
        recommended: 'Önerilen',
        currentPlan: 'Mevcut planın',
        free: 'Ücretsiz',
        perMonth: '/ay',
        footer: 'Yükseltme anında uygulanır (otomatik orantılı), düşürme dönem sonunda.',
        cancelSub: 'Otomatik yenilemeyi iptal et',
        cancelling: 'İptal ediliyor…',
        cancelHint: 'Plan, süresi dolana kadar saklanır; sonra FREE olur.',
        cancelConfirm: 'Otomatik yenileme iptal edilsin mi? Plan, bitiş tarihine kadar saklanır, sonra otomatik olarak FREE olur.',
        cancelOk: 'Otomatik yenileme iptal edildi. Plan, süresi dolana kadar aktif kalır.',
        cancelFail: 'İptal edilemedi — biraz sonra tekrar dene',
        activateFail: 'Plan aktive edilemedi',
        activatedMsg: (id) => `${id} planı aktive edildi!`,
        upgradedMsg: (id, c) => `${id} planına yükseltildi! Uygulanan kredi: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `${id} planına düşürme, mevcut planın sonunda yapılacak.`,
        activatingShort: (id) => `⏱ ${id} aktive ediliyor...`
    },
    indonesio: {
        title: 'Pilih paketmu',
        beta: 'Mode beta — aktivasi instan, tanpa pembayaran. Coba semua fitur.',
        freeLeft: (n) => `${n.toLocaleString()} kursi FREE tersisa`,
        freeOut: 'Kursi FREE habis — pilih paket berbayar',
        close: 'Tutup',
        loading: 'Memuat paket...',
        loadFail: 'Gagal memuat paket',
        activating: 'Mengaktifkan...',
        activeNow: 'Paket aktif',
        activateFree: 'Aktifkan gratis',
        activateBeta: 'Aktifkan (beta)',
        pay: 'Bayar',
        recommended: 'Direkomendasikan',
        currentPlan: 'Paketmu saat ini',
        free: 'Gratis',
        perMonth: '/bln',
        footer: 'Setiap perubahan berlaku langsung saat upgrade (proporsional otomatis), atau di akhir periode saat downgrade.',
        cancelSub: 'Batalkan perpanjangan otomatis',
        cancelling: 'Membatalkan…',
        cancelHint: 'Paket dipertahankan sampai kedaluwarsa; lalu kembali ke FREE.',
        cancelConfirm: 'Batalkan perpanjangan otomatis? Paket dipertahankan sampai tanggal akhirnya, lalu otomatis ke FREE.',
        cancelOk: 'Perpanjangan otomatis dibatalkan. Paket tetap aktif sampai kedaluwarsa.',
        cancelFail: 'Tidak dapat dibatalkan — coba lagi sebentar',
        activateFail: 'Tidak dapat mengaktifkan paket',
        activatedMsg: (id) => `Paket ${id} diaktifkan!`,
        upgradedMsg: (id, c) => `Diupgrade ke ${id}! Kredit diterapkan: ${c.toFixed(2)}€.`,
        downgradeMsg: (id) => `Downgrade ke ${id} dijadwalkan di akhir paket saat ini.`,
        activatingShort: (id) => `⏱ Mengaktifkan ${id}...`
    }
};

function pickL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        const lang = u?.language || 'español';
        return I18N[lang] || I18N.inglés;
    } catch { return I18N.inglés; }
}

// Lee el accountType desde localStorage (fallback si el componente padre
// no pasa `currentUser`). Decisión 3-voces 2026-05-05: marketingLabels +
// hospitalityFeatures se resuelven por accountType ('personal'|'business').
function getAccountType() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return u?.accountType === 'business' ? 'business' : 'personal';
    } catch { return 'personal'; }
}

function PlansModal({ onClose, highlightPlan = null, currentUser }) {
    const L = pickL();
    const [plans, setPlans] = useState([]);
    const [betaMode, setBetaMode] = useState(true);
    const [freeAvailable, setFreeAvailable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(null);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const { plan: myPlan, refresh } = usePlan();

    // ESC + Android back cierran el modal
    useModalClose(onClose, true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get('/plans/all');
                setPlans(data.plans || []);
                setBetaMode(!!data.betaMode);
                setFreeAvailable(data.freeAvailableCount);
            } catch (e) {
                setError(L.loadFail);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCancelSubscription = async () => {
        if (!window.confirm(L.cancelConfirm)) return;
        setActivating('__cancel__');
        setError(null);
        setMessage(null);
        try {
            await api.post('/plans/cancel');
            setMessage(L.cancelOk);
            window.dispatchEvent(new Event('kaixo-plan-changed'));
            refresh();
        } catch (e) {
            setError(friendlyError(e, L.cancelFail));
        } finally {
            setActivating(null);
        }
    };

    const handleActivate = async (planId) => {
        console.log('[PlansModal] Click activar:', planId);
        setMessage(L.activatingShort(planId));
        setActivating(planId);
        setError(null);
        try {
            const { data } = await api.post('/plans/activate', { planId });
            console.log('[PlansModal] respuesta:', data);
            if (data.success === false) {
                setError(data.message || L.activateFail);
                setMessage(null);
            } else if (data.mode === 'paid' && data.checkoutUrl) {
                // En nativo (Android/iOS) abrir Stripe Checkout en navegador
                // EXTERNO. Google Play exige que las compras de bienes/servicios
                // digitales no se procesen dentro de la app vía métodos de pago
                // alternativos a Google Play Billing. Abrir el navegador del
                // sistema con _system mueve el flujo de pago FUERA de la app,
                // que es la práctica permitida (igual que Spotify, Netflix, etc.).
                // Cuando se implemente IAP nativo (sprint posterior), se cambia
                // a Google Play Billing aquí.
                const isNative = !!(window.Capacitor?.isNativePlatform?.());
                if (isNative) {
                    try {
                        const { Browser } = await import('@capacitor/browser');
                        await Browser.open({ url: data.checkoutUrl });
                    } catch {
                        // Fallback si el plugin Browser no está disponible
                        window.open(data.checkoutUrl, '_system');
                    }
                } else {
                    window.location.href = data.checkoutUrl;
                }
            } else {
                const kind = data.kind || 'activated';
                if (kind === 'upgrade') setMessage(L.upgradedMsg(planId, data.credited || 0));
                else if (kind === 'downgrade_scheduled') setMessage(L.downgradeMsg(data.willChangeTo));
                else setMessage(L.activatedMsg(planId));
                window.dispatchEvent(new Event('kaixo-plan-changed'));
                refresh();
            }
        } catch (e) {
            console.error('[PlansModal] error:', e);
            setError(friendlyError(e, L.activateFail));
            setMessage(null);
        } finally {
            setActivating(null);
        }
    };

    const myTier = myPlan?.tier || 'FREE';
    // Resolución del contexto comercial. Si currentUser viene como prop, usamos
    // su accountType; si no, leemos el de localStorage. Determina:
    //   1. Qué label mostrar (`marketingLabels.business` vs `label`)
    //   2. Si renderizar el bloque `hospitalityFeatures` o no
    const accountType = currentUser?.accountType || getAccountType();
    const ordered = plans.slice().sort((a, b) => {
        const order = ['FREE', 'STARTER', 'GROWTH', 'PRO', 'SCALE'];
        return order.indexOf(a.id) - order.indexOf(b.id);
    });

    return ReactDOM.createPortal(
        <div className="plans-overlay" onClick={onClose}>
            <div className="plans-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plans-header">
                    <div>
                        <h2>{L.title}</h2>
                        {betaMode && (
                            <p className="plans-beta-banner">{L.beta}</p>
                        )}
                        {freeAvailable !== null && (
                            <p className="plans-free-counter">
                                {freeAvailable > 0 ? L.freeLeft(freeAvailable) : L.freeOut}
                            </p>
                        )}
                    </div>
                    <button className="plans-close" onClick={onClose} aria-label={L.close}>
                        <Icons.Close size={20} />
                    </button>
                </div>

                {loading && <div className="plans-center">{L.loading}</div>}
                {error && <div className="plans-alert plans-alert-error">{error}</div>}
                {message && <div className="plans-alert plans-alert-success">{message}</div>}

                {!loading && !error && (
                    <div className="plans-grid">
                        {ordered.map(p => {
                            const isCurrent = p.id === myTier;
                            const isHighlighted = p.id === highlightPlan || p.popular;
                            return (
                                <div
                                    key={p.id}
                                    className={`plan-card ${isHighlighted ? 'plan-card-popular' : ''} ${isCurrent ? 'plan-card-current' : ''}`}
                                >
                                    {p.popular && <div className="plan-badge-popular">{L.recommended}</div>}
                                    {isCurrent && <div className="plan-badge-current">{L.currentPlan}</div>}

                                    <h3 className="plan-name">
                                        {(accountType === 'business' && p.marketingLabels?.business) ? p.marketingLabels.business : p.label}
                                    </h3>
                                    <div className="plan-price">
                                        {p.priceEur === 0 ? (
                                            <span className="plan-price-free">{L.free}</span>
                                        ) : (
                                            <>
                                                <span className="plan-price-amount">{p.priceEur}€</span>
                                                <span className="plan-price-period">{L.perMonth}</span>
                                            </>
                                        )}
                                    </div>
                                    <p className="plan-description">{p.description}</p>

                                    <ul className="plan-features">
                                        {(p.highlights || []).map((h, i) => (
                                            <li key={i}>
                                                <span className="plan-check">✓</span> {h}
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Bullets exclusivos hospitality — visible solo si
                                        accountType==='business' y el plan tiene features
                                        hospitalityFeatures definidos en plansConfig.js. */}
                                    {accountType === 'business' && (p.hospitalityFeatures || []).length > 0 && (
                                        <ul className="plan-features plan-hospitality-features">
                                            <li style={{ fontWeight: 700, marginTop: 6, marginBottom: 4, color: '#F5A000', listStyle: 'none' }}>
                                                Exclusivo Hospitality
                                            </li>
                                            {p.hospitalityFeatures.map((h, i) => (
                                                <li key={`hosp-${i}`}>
                                                    <span className="plan-check">★</span> {h}
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    <button
                                        className={`plan-activate-btn ${isCurrent ? 'plan-btn-current' : ''}`}
                                        disabled={activating === p.id || isCurrent}
                                        onClick={() => handleActivate(p.id)}
                                    >
                                        {activating === p.id ? L.activating :
                                            isCurrent ? L.activeNow :
                                                betaMode ? (p.priceEur === 0 ? L.activateFree : L.activateBeta) :
                                                    p.priceEur === 0 ? L.activateFree : `${L.pay} ${p.priceEur}€`
                                        }
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="plans-footer">
                    <p>{L.footer}</p>
                    {/* Botón explícito para cancelar la suscripción — Apple
                        y Google exigen que sea visible y a 1-2 taps. Solo
                        aparece si el usuario tiene un plan de pago activo
                        con auto-renovación habilitada. */}
                    {myPlan && myPlan.tier && myPlan.tier !== 'FREE' && myPlan.autoRenew !== false && (
                        <div style={{ textAlign: 'center', marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                            <button
                                className="plans-cancel-btn"
                                onClick={handleCancelSubscription}
                                disabled={activating === '__cancel__'}
                                style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(220,38,38,0.4)',
                                    color: '#dc2626',
                                    padding: '8px 18px',
                                    borderRadius: 22,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit'
                                }}
                            >
                                {activating === '__cancel__' ? L.cancelling : L.cancelSub}
                            </button>
                            <p style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                                {L.cancelHint}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

export default PlansModal;
